import type { ComparisonCategoryKey } from "@repo/shared/api";
import type { SectionTier } from "@repo/shared/hooks";
import { DetailedPlayer } from "@repo/shared/types";
import { useState } from "react";

import { AttributesCompareSection } from "./AttributesCompareSection";
import { InfoCompareSection } from "./InfoCompareSection";
import { Mode } from "./ModeToggle";
import { PlayStylesCompareSection } from "./PlayStylesCompareSection";
import { RolesCompareSection } from "./RolesCompareSection";

const SECTION_KEYS = ["playstyles", "attributes", "info", "roles"] as const;
type SectionKey = (typeof SECTION_KEYS)[number];

/** Open/closed state for the four compare sections, all open initially. */
export const useOpenSections = () => {
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>(
    { playstyles: true, attributes: true, info: true, roles: true },
  );
  const toggleSection = (key: SectionKey) => () =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  return { openSections, toggleSection };
};

/**
 * The four compare sections in canonical order on a shared column axis — the
 * desktop page and the mobile screen previously each spelled this list out.
 */
export const CompareSections = ({
  players,
  gridStyle,
  sectionTiers,
  categoryWinners,
  mode,
  openSections,
  toggleSection,
  pinnedWidth,
}: {
  players: DetailedPlayer[];
  gridStyle: React.CSSProperties;
  /** Per-player tier + winner for an endpoint category (see useCompareData). */
  sectionTiers: (key: ComparisonCategoryKey) => SectionTier[];
  /** Winning column indexes for an endpoint category (see useCompareData). */
  categoryWinners: (key: ComparisonCategoryKey) => Set<number>;
  mode: Mode;
  openSections: Record<SectionKey, boolean>;
  toggleSection: (key: SectionKey) => () => void;
  /** Mobile: sticky-left width for pinned rows in the shared scroller. */
  pinnedWidth?: number;
}) => (
  <>
    <PlayStylesCompareSection
      players={players}
      gridStyle={gridStyle}
      tiers={sectionTiers("playStyles")}
      open={openSections.playstyles}
      onToggle={toggleSection("playstyles")}
      mode={mode}
      pinnedWidth={pinnedWidth}
    />
    <AttributesCompareSection
      players={players}
      gridStyle={gridStyle}
      tiers={sectionTiers("igAttributes")}
      open={openSections.attributes}
      onToggle={toggleSection("attributes")}
      mode={mode}
      pinnedWidth={pinnedWidth}
    />
    <InfoCompareSection
      players={players}
      gridStyle={gridStyle}
      categoryWinners={categoryWinners}
      open={openSections.info}
      onToggle={toggleSection("info")}
      mode={mode}
      pinnedWidth={pinnedWidth}
    />
    <RolesCompareSection
      players={players}
      gridStyle={gridStyle}
      open={openSections.roles}
      onToggle={toggleSection("roles")}
      mode={mode}
      pinnedWidth={pinnedWidth}
    />
  </>
);
